import { USER_ACTIVE_STATE } from '@thoughtspot/blink-constants';
import { logger } from '@thoughtspot/client-state-util/esm/utils';
import { translate as t } from '@thoughtspot/i18n';
import { UserAction, userActionTypeEnum } from '@thoughtspot/metrics';
import {
    getClusterId,
    getPartnerServiceConfig,
    getSessionInfo,
    getState,
    getUserEmail,
    getUserGuid,
    getUserProperties,
    hasAdminPrivileges,
    isCopilotEnabled,
    isLoggedIn,
    PasswordStrategy,
    UserProperties,
} from '@thoughtspot/session-service';
import { getClientId, getIdpDomain } from '@thoughtspot/system-config-service';
import React from 'react';
import { getTranslatedInfoForMessageCode } from '/@utils/error-handling/error-strings-mapping';
import {
    BACKEND_SESSION_EXPIRY_CODE,
    getBaseOktaActivationURL,
    getValidationObjects,
    userIdParam,
} from '../common';
import styles from './account-activation.module.scss';

/* Remove idpDomain from userName */
export const getUsernameWithoutDomain = (userName: string): string => {
    if (userName.lastIndexOf('@') !== -1) {
        return userName.substring(0, userName.lastIndexOf('@'));
    }
    return userName;
};

export const commonFetchCall = (url: string, inputData: object) => {
    return fetch(`${getBaseOktaActivationURL(getClientId())}/${url}`, {
        headers: {
            'content-type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify(inputData),
        mode: 'cors',
        credentials: 'include',
    });
};

export const initialError = () => {
    return {
        create: {
            touched: false,
            error: false,
        },
        confirm: {
            touched: false,
            error: false,
        },
        email: {
            touched: false,
            error: false,
        },
    };
};

export const hasCompanyName = (): boolean => {
    const userProperties: UserProperties = getUserProperties();
    return !!(userProperties && userProperties.companyName);
};

export const getCompanyName = (): string => {
    const userProperties: UserProperties = getUserProperties();
    return hasCompanyName() ? userProperties.companyName : '';
};

export const getBackendWhitelistIPs = (): string => {
    const config = getPartnerServiceConfig();
    return config ? config.backendWhitelistIPs : '';
};

export const isActivated = (): boolean => {
    const state = getState();
    return !!(state && state === USER_ACTIVE_STATE.ACTIVE);
};

export const initialValidation = () => {
    return {
        errorText: '',
        toolTipText: '',
        rules: [...getValidationObjects(t)],
    };
};

export const getSessionExpiryAlertContent = (
    setTokenInvalid: (arg0: boolean) => void,
    isOnlyForOkta: any,
) => {
    setTokenInvalid(true);
    if (isOnlyForOkta) {
        return t('accountActivation.oktaSessionExpiry', { 1: '#/login' });
    }
    return t('accountActivation.sessionExpiry', { 1: '#/login' });
};

export const getPasswordRequirements = (
    PASSWORD_STRATEGY: PasswordStrategy,
): string => {
    return PASSWORD_STRATEGY === PasswordStrategy.GUESS_ESTIMATE
        ? t('freetrial.passwordrequirements')
        : '';
};

export const logoutIfSessionExists = async (
    doLogout: () => PromiseLike<any>,
): Promise<any> => {
    const sessionInfo = getSessionInfo();
    // TODO(ambu): need to do this string check because we store error
    // responses from backend in session info. Will come back to this.
    if (!sessionInfo || typeof sessionInfo === 'string') {
        return Promise.reject();
    }
    // perform window reload post doing the logout.
    await doLogout();
    window.location.reload();
    return Promise.reject();
};

export const shouldRedirectToHomepage = (
    readClientQueryParamsFromPath: any,
): boolean => {
    return (
        !!isLoggedIn() &&
        readClientQueryParamsFromPath().get(userIdParam) === getUserGuid() &&
        !!isActivated()
    );
};

export const errorActions = (
    errorResponse: any,
    isOnlyForOkta: any,
    setTokenInvalid: (arg0: boolean) => void,
    setValidation: (arg0: any) => void,
    validation: any,
) => {
    const userAction = new UserAction(
        userActionTypeEnum.ACCOUNT_ACTIVATION_FAILURE,
    );
    const errorCode = errorResponse && errorResponse.code;
    const errorText =
        isOnlyForOkta || errorCode === BACKEND_SESSION_EXPIRY_CODE
            ? getSessionExpiryAlertContent(setTokenInvalid, isOnlyForOkta)
            : (getTranslatedInfoForMessageCode(errorCode).summary as any);
    let toolTipText =
        errorCode !== BACKEND_SESSION_EXPIRY_CODE
            ? t('freetrial.passwordrequirements')
            : '';
    if (isOnlyForOkta) {
        toolTipText = '';
    }
    setValidation({
        ...validation,
        errorText,
        toolTipText,
    });
};

export const accountActivationHeader = () => {
    return (
        <div className={styles.headerContent}>
            <span className={styles.headerContentTitle}>
                {isCopilotEnabled()
                    ? t('copilot.freetrialHeader.title')
                    : t('freetrial.headerTitle')}
            </span>
            <span className={styles.headerContentSubTitle}>
                {t('freetrial.subTitle')}
            </span>
        </div>
    );
};

export const setCreatePasswordError = (
    isError: boolean,
    hasError: any,
    setHasError: (arg0: any) => void,
): void => {
    const createError = { ...hasError };
    createError.create.error = isError;
    setHasError(createError);
};

export const setConfirmPasswordError = (
    isError: boolean,
    hasError: any,
    setHasError: (arg0: any) => void,
): void => {
    const confirmError = { ...hasError };
    confirmError.confirm.error = isError;
    setHasError(confirmError);
};

export const setEmailValidationError = (
    isError: boolean,
    hasError: any,
    setHasError: (arg0: any) => void,
): void => {
    const emailError = { ...hasError };
    emailError.email.error = isError;
    setHasError(emailError);
};

export const activateOktaAccountFailure = (
    setSaving: (arg0: boolean) => void,
    setTokenInvalid: (arg0: boolean) => void,
    isOnlyForOkta: any,
    setValidation: any,
    validation: any,
) => {
    setSaving(false);
    const errorText = getSessionExpiryAlertContent(
        setTokenInvalid,
        isOnlyForOkta,
    );
    setValidation({
        errorText: t(errorText as any),
        toolTipText: t('freetrial.passwordrequirements'),
        rules: validation.rules,
    });
};

export const doShortLivedLoginCB = async (
    readClientQueryParamsFromPath: () => any,
    isOnlyForOkta: any,
    verifyOktaToken: (token: string, id: string) => any,
    shortLivedLogin: (token: any, id: any) => any,
) => {
    const routeParam = readClientQueryParamsFromPath();
    const id = routeParam.get('id').toString();
    const token = routeParam.get('token').toString();
    if (isOnlyForOkta) {
        return verifyOktaToken(token as string, id as string);
    }
    return shortLivedLogin(id, token);
};

// mfa enroll activation satus.
export const MFA_ENROLL_STATUTS = 'MFA_ENROLL';
